import { EmailTemplateModel } from '@/typings/api/emailCenter';
import { F } from '@/utils/intl';
import {
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Alert, Button, Card, Form, InputNumber, Select } from 'antd';
import React from 'react';
import ConditionRow from './ConditionRow';
import { createEmptyCondition, FormSection } from './sections';

import styles from './AutomationsTab.less';

interface SectionCardProps {
  section: FormSection;
  index: number;
  templates: EmailTemplateModel[];
  onChange: (next: FormSection) => void;
  onRemove: () => void;
}

/** One ordered step of the automation: wait → conditions → send template. */
const SectionCard: React.FC<SectionCardProps> = ({
  section,
  index,
  templates,
  onChange,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: section.uid,
    });

  return (
    <div
      ref={setNodeRef}
      className={styles.sectionCard}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <Card
        size="small"
        title={
          <span className={styles.sectionTitle}>
            <Button
              type="text"
              size="small"
              icon={<HolderOutlined />}
              className={styles.dragHandle}
              {...attributes}
              {...listeners}
            />
            <F
              id="pages.emailCenter.automations.section.title"
              defaultMessage="Step {step}"
              values={{ step: index + 1 }}
            />
          </span>
        }
        extra={
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
          />
        }
      >
        {section.unresolvedTemplateName && !section.template_id && (
          <Alert
            className={styles.sectionAlert}
            type="warning"
            showIcon
            message={
              <F
                id="pages.emailCenter.automations.section.unresolvedTemplate"
                defaultMessage='The template "{name}" could not be matched — please pick it again.'
                values={{ name: section.unresolvedTemplateName }}
              />
            }
          />
        )}

        <Form.Item
          label={
            <F
              id="pages.emailCenter.automations.section.waitInDays"
              defaultMessage="Wait (days)"
            />
          }
          className={styles.sectionField}
        >
          <InputNumber
            min={0}
            value={section.wait_in_days}
            onChange={(waitInDays) =>
              onChange({ ...section, wait_in_days: waitInDays ?? 0 })
            }
          />
        </Form.Item>

        <Form.Item
          label={
            <F
              id="pages.emailCenter.automations.section.template"
              defaultMessage="Template"
            />
          }
          className={styles.sectionField}
          required
        >
          <Select
            showSearch
            optionFilterProp="label"
            value={section.template_id}
            onChange={(templateId) =>
              onChange({
                ...section,
                template_id: templateId,
                unresolvedTemplateName: undefined,
              })
            }
            options={templates.map((template) => ({
              value: template.id,
              label: template.name,
            }))}
          />
        </Form.Item>

        <Form.Item
          label={
            <F
              id="pages.emailCenter.automations.section.conditions"
              defaultMessage="Conditions"
            />
          }
          className={styles.sectionField}
        >
          <div className={styles.conditionList}>
            {section.conditions.map((condition, conditionIndex) => (
              <ConditionRow
                // eslint-disable-next-line react/no-array-index-key
                key={conditionIndex}
                value={condition}
                onChange={(next) =>
                  onChange({
                    ...section,
                    conditions: section.conditions.map((entry, entryIndex) =>
                      entryIndex === conditionIndex ? next : entry,
                    ),
                  })
                }
                onRemove={() =>
                  onChange({
                    ...section,
                    conditions: section.conditions.filter(
                      (_, entryIndex) => entryIndex !== conditionIndex,
                    ),
                  })
                }
              />
            ))}
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                onChange({
                  ...section,
                  conditions: [...section.conditions, createEmptyCondition()],
                })
              }
            >
              <F
                id="pages.emailCenter.automations.section.addCondition"
                defaultMessage="Add condition"
              />
            </Button>
          </div>
        </Form.Item>
      </Card>
    </div>
  );
};

export default React.memo(SectionCard);
